import { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  History as HistoryIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { getInviteCodeHistory } from '@services/organizationService';

const InviteCodeHistory = ({ organizationId }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    fetchHistory();
  }, [organizationId]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      setError('');
      const historyData = await getInviteCodeHistory(organizationId);
      setHistory(historyData);
    } catch (err) {
      console.error('Error fetching invite code history:', err);
      console.error('Error details:', err.message, err.code);
      setError(`Failed to load history: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  };

  const getActionIcon = (action) => {
    return action === 'created' ? <AddIcon fontSize="small" /> : <RefreshIcon fontSize="small" />;
  };

  const getActionColor = (action) => {
    return action === 'created' ? 'success' : 'warning';
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress size={24} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Loading history...
        </Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (history.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'background.default' }}>
        <HistoryIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          No history available
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Invite code changes will appear here
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={1} sx={{ overflow: 'hidden' }}>
      <Box
        sx={{
          p: 2,
          bgcolor: 'background.default',
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon color="action" />
          <Typography variant="h6" fontWeight="bold">
            Invite Code History
          </Typography>
          <Chip label={history.length} size="small" />
        </Box>
        <IconButton size="small">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <List sx={{ p: 0 }}>
          {history.map((entry, index) => (
            <Box key={entry.id}>
              {index > 0 && <Divider />}
              <ListItem
                sx={{
                  py: 2,
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      icon={getActionIcon(entry.action)}
                      label={entry.action === 'created' ? 'Created' : 'Regenerated'}
                      size="small"
                      color={getActionColor(entry.action)}
                      variant="outlined"
                    />
                    <Typography variant="body2" color="text.secondary">
                      by <strong>{entry.changedByName}</strong>
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {formatTimestamp(entry.timestamp)}
                  </Typography>
                </Box>

                <Box sx={{ width: '100%', display: 'flex', gap: 2, mt: 1 }}>
                  {entry.oldCode && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Old Code
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          bgcolor: 'error.lighter',
                          px: 1,
                          py: 0.5,
                          borderRadius: 0.5,
                          display: 'inline-block',
                          textDecoration: 'line-through',
                          opacity: 0.7,
                        }}
                      >
                        {entry.oldCode}
                      </Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {entry.action === 'created' ? 'Code' : 'New Code'}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        bgcolor: 'success.lighter',
                        px: 1,
                        py: 0.5,
                        borderRadius: 0.5,
                        display: 'inline-block',
                        fontWeight: 'bold',
                      }}
                    >
                      {entry.newCode}
                    </Typography>
                  </Box>
                </Box>
              </ListItem>
            </Box>
          ))}
        </List>
      </Collapse>
    </Paper>
  );
};

export default InviteCodeHistory;
